import React from "react";
import PropTypes from "prop-types";

/**
 * ProfilePageSkeleton — loading placeholder mirroring the ProfilePage two-column grid.
 *
 * Mirrors `<ProfilePage>`'s grid so the loading state and loaded state share the same layout.
 * The engine supplies skeleton slots for the header, the main column, and the right rail.
 *
 * Unlike `<ProfilePage>`, the `header` slot IS banded here. Each engine ships its own header
 * placeholder rather than rendering through `PersonHeader` / `EntityHeader`, so banding them one
 * by one would mean six edits; and the placeholders are inset cards with no full-bleed banner to
 * preserve, so one wrapper here is both safe and sufficient. It uses the header's own band class
 * because the placeholder inside carries the same 24px inset the loaded header does.
 *
 * This narrows the horizontal shift on load but does not remove it at every width: this wrapper
 * sits in the ordinary page container while the loaded header sits in a card that is 32px wider,
 * so the two reach their maximum at different container widths. The pre-existing shift is
 * unchanged below that point.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.header - Header skeleton slot
 * @param {React.ReactNode} props.children - Main-column skeleton slot
 * @param {React.ReactNode} [props.rightRail] - Right-rail skeleton slot
 */
function ProfilePageSkeleton({ header, children, rightRail = null }) {
  return (
    <>
      <div className="person-header-content">{header}</div>
      <div className="profile-content-band">
        <div className="row mt-4">
          <div className={`profile-main-column ${rightRail ? "col-12 col-md-8 mb-3" : "col mb-3"}`}>{children}</div>
          {rightRail && <div className="profile-rail-column col-12 col-md-4 mb-3">{rightRail}</div>}
        </div>
      </div>
    </>
  );
}

ProfilePageSkeleton.propTypes = {
  header: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  rightRail: PropTypes.node,
};

export default ProfilePageSkeleton;
